/**
 * Camera Capture Component
 * Provides live camera feed and capture functionality
 */

import { CameraOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Space, message } from "antd";
import React, { useRef, useEffect, useState } from "react";

interface CameraCaptureProps {
	onCapture: (photo: Blob) => void;
	onCancel: () => void;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
	onCapture,
	onCancel,
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		startCamera();
		return () => {
			stopCamera();
		};
	}, []);

	const startCamera = async () => {
		try {
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: "environment", // Use back camera on mobile
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
			});

			if (videoRef.current) {
				videoRef.current.srcObject = mediaStream;
				setStream(mediaStream);
			}
		} catch (err: any) {
			console.error("Camera access error:", err);
			setError(
				"Unable to access camera. Please check your browser permissions.",
			);
			message.error("Unable to access camera");
		}
	};

	const stopCamera = () => {
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
		}
	};

	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) return;

		const video = videoRef.current;
		const canvas = canvasRef.current;

		// Set canvas dimensions to match video
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		// Draw video frame to canvas
		const ctx = canvas.getContext("2d");
		if (ctx) {
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

			// Convert canvas to blob
			canvas.toBlob(
				(blob) => {
					if (blob) {
						stopCamera();
						onCapture(blob);
					}
				},
				"image/jpeg",
				0.95,
			);
		}
	};

	const handleCancel = () => {
		stopCamera();
		onCancel();
	};

	if (error) {
		return (
			<Alert
				type="error"
				message="Camera Error"
				description={error}
				showIcon
				action={
					<Button size="small" onClick={handleCancel}>
						Close
					</Button>
				}
			/>
		);
	}

	return (
		<div className="camera-container">
			<video
				ref={videoRef}
				autoPlay
				playsInline
				muted
				style={{
					width: "100%",
					maxWidth: "640px",
					borderRadius: "8px",
					display: "block",
				}}
			/>
			<canvas ref={canvasRef} style={{ display: "none" }} />

			<div className="camera-controls">
				<Space size="large">
					<Button
						type="primary"
						size="large"
						icon={<CameraOutlined />}
						onClick={capturePhoto}
					>
						Capture
					</Button>
					<Button
						size="large"
						icon={<CloseCircleOutlined />}
						onClick={handleCancel}
					>
						Cancel
					</Button>
				</Space>
			</div>
		</div>
	);
};
